"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  finishSpecsForDefinitions,
  finishSpecsForGame as resolveFinishSpecsForGame,
  type FinishSpec,
} from "modules/cards/model/finishBehavior";
import {
  gamesForCapability,
  gamesForPricingVariant,
} from "modules/common/game-capabilities";
import type {
  GameCapability,
  GameDefinition,
  GameFacetDefinition,
  GameId,
  GamePricingVariant,
} from "modules/common/model/models";
import {
  validateFacetContracts,
  type FacetContract,
} from "modules/common/cardFacets";

interface GameRegistryContextValue {
  definitions: readonly GameDefinition[];
  sharedFacets: readonly GameFacetDefinition[];
  facetContract: FacetContract;
  finishSpecs: readonly FinishSpec[];
  finishSpecsForGame(gameId: GameId): readonly FinishSpec[];
}

const GameRegistryContext = createContext<GameRegistryContextValue | null>(
  null
);
const NO_FINISHES: readonly FinishSpec[] = [];

export function GameRegistryProvider({
  definitions,
  sharedFacets,
  children,
}: {
  definitions: GameDefinition[];
  sharedFacets: GameFacetDefinition[];
  children: ReactNode;
}) {
  const finishSpecs = useMemo(
    () => finishSpecsForDefinitions(definitions),
    [definitions]
  );
  const facetContract = useMemo(
    () => ({ definitions, sharedFacets }),
    [definitions, sharedFacets]
  );
  validateFacetContracts(facetContract);
  const finishSpecsByGame = useMemo(
    () =>
      new Map(
        definitions.map((definition) => [
          definition.id,
          resolveFinishSpecsForGame(definition),
        ])
      ),
    [definitions]
  );
  const finishSpecsForGame = useCallback(
    (gameId: GameId) => finishSpecsByGame.get(gameId) || NO_FINISHES,
    [finishSpecsByGame]
  );
  const value = useMemo(
    () => ({
      definitions,
      sharedFacets,
      facetContract,
      finishSpecs,
      finishSpecsForGame,
    }),
    [definitions, sharedFacets, facetContract, finishSpecs, finishSpecsForGame]
  );

  return (
    <GameRegistryContext.Provider value={value}>
      {children}
    </GameRegistryContext.Provider>
  );
}

export function useGameRegistry(): GameRegistryContextValue {
  const registry = useContext(GameRegistryContext);
  if (!registry) {
    throw new Error("useGameRegistry must be used within GameRegistryProvider");
  }
  return registry;
}

export function useGamesForCapability(capability: GameCapability) {
  const registry = useGameRegistry();
  const games = useMemo(
    () => gamesForCapability(capability, registry.definitions),
    [capability, registry.definitions]
  );
  return { games };
}

export function useGamesForPricingVariant(variant: GamePricingVariant) {
  const registry = useGameRegistry();
  const games = useMemo(
    () => gamesForPricingVariant(variant, registry.definitions),
    [registry.definitions, variant]
  );
  return { games };
}
